// src/ui/Avatar.jsx — one robust avatar for users AND groups.
// - user  : profileImage (many field fallbacks) → falls back to a colored initial
// - group : groupImage → else a COLLAGE of members' photos → else colored initial
// - broken/unloadable URLs degrade to the initial (never a broken-image icon)
// - group badge (people icon) overlaid bottom-right
import React, { useEffect, useState } from 'react';
import { getUserProfile } from '../services/users';
import { isNoteToSelf } from '../services/chats';
import { useBlocking, isBlockedEither, blockedEitherNow } from '../services/blocking';   // 🚫
import { activeMembers, COLLAGE_MAX } from '../services/groupMembers';
import { DocText } from './icons';
/* 🧑‍🎨 the 3D avatar: live over the picture when a host passes `live` */
import LiveAvatar from './LiveAvatar';
import { hasAvatar } from '../services/avatar3d';

// 🩶 One color for every initial-avatar — the phone's blue-grey (src/utils/avatarPlaceholder.ts
//    in the app: '#5D6D85', the same in light and dark). It was black, which on the dark
//    theme was a hole in the page. (The old per-user hash palette — blue/orange/green/etc —
//    is kept here commented in case it ever returns:
//    ['#0A84FF','#34C759','#FF9500','#AF52DE','#FF2D55','#5AC8FA','#FFCC00','#5856D6','#FF6482','#30B0C7'])
const AVATAR_FALLBACK_BG = '#5D6D85';
/* Note to Self keeps its own black disc with the document icon, as on the phone */
const NOTE_SELF_BG = '#000000';
function colorFor() { return AVATAR_FALLBACK_BG; }

export function resolveName(profileOrChat) {
  const p = profileOrChat || {};
  // the mobile app stores a GROUP's title in `name` (groupName is only used by call payloads)
  return p.fullName || p.name || p.groupName || p.username || p.displayName || 'Allo user';
}
/** Only URLs a BROWSER can actually load. A phone-local file://, content:// or ph://
 *  path is meaningless here and is exactly what renders as a broken image. */
export function webSafeUrl(u) {
  if (!u || typeof u !== 'string') return null;
  return /^(https?:)?\/\//.test(u) || u.startsWith('data:') || u.startsWith('blob:') ? u : null;
}
export function resolveImage(profile) {
  const p = profile || {};
  return webSafeUrl(p.profileImage) || webSafeUrl(p.groupImage) || webSafeUrl(p.image) || webSafeUrl(p.photoURL) || webSafeUrl(p.avatar);
}
export function groupTitle(chat) { return chat?.name || chat?.groupName || 'Group'; }
function initial(name = '') { const c = (name || '').trim()[0]; return c ? c.toUpperCase() : '#'; }

function PeopleBadge() {
  return (
    <span style={{
      position: 'absolute', bottom: -2, right: -2, width: 20, height: 20, borderRadius: 10,
      background: 'var(--allo-blue)', border: '2px solid var(--bg-sidebar)', display: 'grid', placeItems: 'center',
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.7 0-6 1.3-6 4v2h8v-2c0-1 .4-1.9 1-2.6A9 9 0 0 0 8 13Zm8 0a7 7 0 0 0-1 .1c1 .8 1.6 1.8 1.6 2.9v2H22v-2c0-2.7-3.3-4-6-4Z" /></svg>
    </span>
  );
}

/** <img> that vanishes (→ shows the fallback behind it) if the URL fails to load. */
function SafeImg({ src, style }) {
  const [dead, setDead] = useState(false);
  useEffect(() => { setDead(false); }, [src]);
  if (!src || dead) return null;
  return <img src={src} alt="" style={style} onError={() => setDead(true)} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 THE TILES MOVED. A group with no photo of its own draws its members, and
   the members walked from one side to the other two, three, four times before
   settling — in the chat list AND in the header of the group you had just
   opened.

   Two faults, and they multiplied each other.

   🟥 1. THE LAYOUT WAS DECIDED BY WHAT HAD ARRIVED. The collage was built from
      a list of URLS: the chat document's partial `participantImages` cache
      painted first, then the members' own profiles replaced it. One URL is a
      single full-width tile; two is a row; three puts the first across the top;
      four is a quarter grid. So every time the list of photos grew, every tile
      that was already on screen moved somewhere else. A member with no photo
      contributed nothing and silently shifted everyone after them.

   🟥 2. AND IT RAN AGAIN ON EVERY SNAPSHOT. The effect's dependencies were
      `chat.participantImages` and a `useMemo` over `chat.participants` — both
      OBJECT IDENTITIES, and `watchChats` rebuilds every chat object from
      scratch on each snapshot, so neither ever compared equal. Opening a group
      writes presence and a read watermark onto that same chat document, which
      is what makes the shuffle happen exactly when you walk in; `watchChats`
      also emits twice by design. Each emit re-ran the whole ladder from the
      one-tile cache back up to four.

   🟢 SO THE COLLAGE IS BUILT FROM THE MEMBERS, IN THE GROUP'S OWN ORDER. Who is
      in the group is known synchronously, from the chat document, before a
      single photo has loaded — so the number of tiles and the position of each
      member is settled at the first paint and nothing that arrives later can
      move them. A photo drops into the tile its member already owns; a member
      without one keeps their tile and shows their initial.

   🟢 AND THE EFFECT DEPENDS ON VALUES. A joined string of member ids compares
      by content, so a snapshot that changes nothing re-runs nothing.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The grid a given number of members makes, and which tile spans the top.
 *
 * 🟥 THIS IS THE SHAPE THAT KEPT CHANGING. One tile is full width, two is a
 *    row, three puts the first across the top, four is a quarter grid — so the
 *    count deciding the shape had to stop being "how many photos have loaded"
 *    and start being "how many people are in the group", which is known before
 *    anything loads and does not change while you look at it.
 */
export function collageGrid(count) {
  const n = Math.max(0, Math.min(Number(count) || 0, 4));
  return {
    n,
    columns: n === 1 ? '1fr' : '1fr 1fr',
    rows: n <= 2 ? '1fr' : '1fr 1fr',
    /** the index that spans both columns, or -1 */
    spans: n === 3 ? 0 : -1,
  };
}

/**
 * One tile per member, in the group's own order, whether or not their photo has
 * arrived — see the note above for why this is a list of PEOPLE, not of URLs.
 *
 * 🟢 THE CHAT DOCUMENT'S CACHE MAY FILL A TILE AND MUST NEVER CREATE ONE. It is
 *    partial by nature (often a single member), and letting it decide the count
 *    is what made the first paint disagree with every later one.
 */
export function collageTiles(memberIds, images, names, fetched) {
  const imgs = images || {};
  const nms = names || {};
  const got = fetched || {};
  return (memberIds || []).slice(0, COLLAGE_MAX).map((uid) => ({
    uid,
    url: got[uid]?.url || webSafeUrl(imgs[uid]) || null,
    /* the chat document's cached name first — it is the one the row itself
       shows — then whatever the member's own profile said */
    name: nms[uid] || got[uid]?.name || '',
  }));
}

/** Up to 4 members in a WhatsApp-style tiled collage — one tile per member. */
function Collage({ tiles }) {
  const g = collageGrid(tiles.length);
  const n = g.n;
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'grid', gap: 1,
      gridTemplateColumns: g.columns,
      gridTemplateRows: g.rows,
    }}>
      {tiles.slice(0, COLLAGE_MAX).map((t, i) => (
        /* 🟢 KEYED BY THE MEMBER, NOT BY THE INDEX. React reuses a tile whose key
           it recognises; keyed by position, a member arriving or leaving would
           re-point every <img> after them and blink the whole collage. */
        <div key={t.uid} style={{
          overflow: 'hidden', background: AVATAR_FALLBACK_BG,
          gridColumn: i === g.spans ? '1 / span 2' : undefined,
          display: 'grid', placeItems: 'center',
        }}>
          {t.url
            ? <SafeImg src={t.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            /* 🟢 A NAMELESS MEMBER KEEPS THEIR TILE AND SAYS NOTHING. `initial('')`
               answers '#', and a hash standing in for a person is worse than the
               plain square — the tile's job here is to hold the position. */
            : t.name ? <span style={{ color: '#fff', fontWeight: 700, fontSize: n === 1 ? '1em' : '.62em' }}>{initial(t.name)}</span> : null}
        </div>
      ))}
    </div>
  );
}

export default function Avatar({ profile, chat, size = '', online, showGroupBadge = true, myUid, peerUid, live = null }) {
  const isGroup = !!chat?.isGroup;
  // 🟢 NOTE TO SELF — same treatment as the app (ChatsTab ~2570): a black tile with a
  //    white document glyph, never a photo and never an initial.
  const noteSelf = isNoteToSelf(chat, myUid);
  const name = isGroup ? groupTitle(chat) : resolveName(profile);
  /* 🚫 A BLOCK HIDES THE PICTURE, in both directions — this is the one avatar
     every list, header and pane draws, so this is where the rule lives. The
     initial stays; only the photo goes. */
  const blocking = useBlocking();
  const peerId = peerUid || profile?.uid || profile?.id || null;
  const hidden = !isGroup && !noteSelf && isBlockedEither(blocking, peerId);
  // 1:1 → the peer's own profile photo, falling back to the chat's cached
  // participantImages entry (covers a profile doc we couldn't read).
  const directUrl = isGroup
    ? (webSafeUrl(chat.groupImage) || webSafeUrl(chat.image))
    : (hidden ? null : (resolveImage(profile) || webSafeUrl(chat?.participantImages?.[peerUid])));

  /* 🟢 GROUP COLLAGE: no group photo → tile the MEMBERS. See the note above
     Collage for why this is a list of PEOPLE and not a list of URLs.

     🟢 A STRING DEPENDENCY, BECAUSE A CHAT OBJECT IS NEW ON EVERY SNAPSHOT.
        `watchChats` rebuilds each chat from `d.data()`, so `chat.participants`
        never compares equal to itself and an effect keyed on it re-runs on every
        presence write, every read watermark, every emit — which is every second
        or two in a chat you are sitting in. */
  /* 🟥 AND THE SAME MEMBERS THE PHONE DRAWS. GroupCollageAvatar.tsx takes
     `participants` minus `leftParticipants` and keeps the current user in it —
     this filtered `myUid` out and ignored the leavers, so the same group wore a
     two-tile avatar here and a three-tile one on the phone, and a member who had
     left the group still had a face on the web. One group, one picture. */
  /* 🟢 THE SHARED RULE, generated from the phone's own — the collage, the pager
     that opens when you tap it, and the phone's GroupCollageAvatar all have to
     count the same people, or "1 of 4" is a lie about the four tiles you just
     tapped. See services/groupMembers.js. */
  const memberIdsKey = activeMembers(chat).join(',');
  const memberIds = React.useMemo(() => (memberIdsKey ? memberIdsKey.split(',') : []), [memberIdsKey]);

  /** uid → photo, from the members' own profiles. */
  const [fetched, setFetched] = useState({});
  useEffect(() => {
    let live = true;
    if (!isGroup || directUrl || !memberIds.length) return undefined;
    /* only the four that can be drawn — the old code fetched eight to fill four
       slots, which is what made the tile COUNT depend on who had a photo */
    Promise.all(memberIds.slice(0, COLLAGE_MAX).map((uid) => getUserProfile(uid).catch(() => null))).then((ps) => {
      if (!live) return;
      const next = {};
      ps.forEach((prof, i) => {
        const u = blockedEitherNow(memberIds[i]) ? null : resolveImage(prof);   // 🚫 a blocked member is an initial
        const nm = prof ? resolveName(prof) : '';
        if (u || (nm && nm !== 'Allo user')) next[memberIds[i]] = { url: u || null, name: u ? '' : nm };
      });
      /* 🟢 THE SAME ANSWER IS NOT AN UPDATE. Profiles are cached, so a re-run
         resolves to an identical map — setting it anyway would re-render every
         row in the list for nothing. */
      setFetched((prev) => {
        const a = Object.keys(prev); const b = Object.keys(next);
        if (a.length === b.length
          && b.every((k) => prev[k] && prev[k].url === next[k].url && prev[k].name === next[k].name)) return prev;
        return next;
      });
    });
    return () => { live = false; };
  }, [isGroup, directUrl, memberIdsKey, memberIds]);

  /* The chat document's own cache paints before any profile has been read; it is
     partial by nature (often one member), which is exactly why it may fill tiles
     but must never decide how many there are. */
  const imagesKey = JSON.stringify(chat?.participantImages || {});
  const namesKey = JSON.stringify(chat?.participantNames || {});
  const tiles = React.useMemo(
    () => collageTiles(memberIds, JSON.parse(imagesKey), JSON.parse(namesKey), fetched),
    [memberIds, fetched, imagesKey, namesKey],
  );

  /* 🟢 A COLLAGE OF NOTHING IS NOT A COLLAGE. With no photo anywhere in the
     group the honest picture is still the group's own initial — four letters in
     a grid says less than one does. The moment any member's photo is known the
     collage appears, at its final size, once. */
  const showCollage = isGroup && !directUrl && tiles.some((t) => t.url);

  if (noteSelf) {
    return (
      <div className="avatar-wrap">
        <div className={`avatar ${size} note-self`} style={{ background: NOTE_SELF_BG }}>
          <DocText />
        </div>
      </div>
    );
  }

  const still = (
      <div className={`avatar ${size}`}
        style={{ background: directUrl || showCollage ? '#2a3550' : colorFor(name), position: 'relative', overflow: 'hidden' }}>
        {/* fallback sits underneath; images cover it when they load */}
        <span style={{ color: '#fff', fontWeight: 700 }}>{initial(name)}</span>
        {directUrl && (
          <SafeImg src={directUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {showCollage && <Collage tiles={tiles} />}
      </div>
  );
  /* 🧑‍🎨 A 1:1 WITH AN AVATAR, where the host has said so with `live`: the picture
     stays, and the live view (roll it; it peeks out when they come online, under
     the same presence gate as the dot) is laid over it. See LiveAvatar.jsx. */
  const px = size === 'sm' ? 40 : size === 'lg' ? 96 : 50;
  const liveOk = !!live && !isGroup && !hidden && hasAvatar(profile);
  return (
    <div className="avatar-wrap">
      {liveOk ? (
        <LiveAvatar
          profile={profile}
          size={px}
          viewKey={live.viewKey || `avatar:${peerId || 'peer'}`}
          online={!!live.online}
          canSeeOnline={!!live.canSeeOnline}
          priority={live.priority}
          warm={!!live.warm}
          motions={live.motions !== false}
          still={still}
        />
      ) : still}
      {isGroup && showGroupBadge && <PeopleBadge />}
      {!isGroup && online && <span className="presence-dot" />}
    </div>
  );
}
